import { useCallback, useEffect, useState } from 'react';
import { Percent, Loader2, Check, RotateCcw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';
import { useToast } from '@/lib/hooks/use-toast';
import axiosInstance from '@/lib/api/axios-instance';

/**
 * Parámetros de ganancia: el margen que se suma al costo para llegar al precio.
 *
 * Es lo que Delicia pidió tres veces en la reunión —«manejar precio de venta»,
 * «ver precios de venta donde se manejan costos», «la parte comercial ver precios
 * de venta»— y lo único de ese pedido que faltaba. El cálculo ya existía; no había
 * dónde configurarlo, así que los márgenes vivían fijos en el código.
 *
 * **Es una sugerencia, no una imposición.** El precio queda editable en la
 * cotización y manda lo que escriba el vendedor. Esto define el punto de partida.
 *
 * **Sin IA, a propósito.** Un precio sugerido tiene que ser predecible y
 * explicable —«costo × margen configurado»— para poder defenderlo frente a un
 * cliente. La salida de un modelo no se puede explicar así.
 */

type LegType = 'INTERNATIONAL' | 'INLAND_ORIGIN' | 'INLAND_DESTINATION' | 'HANDLING';

interface MarginRow {
  legType: LegType;
  marginPercent: number;
  isDefault: boolean;
}

/** Cómo se llama cada tramo para quien lo lee, y qué es. */
const ETIQUETAS: Record<LegType, { titulo: string; ayuda: string }> = {
  INTERNATIONAL: {
    titulo: 'Marítimo / aéreo internacional',
    ayuda: 'El tramo de origen al puerto de tránsito. En el tarifario es el "Profit Mínimo 10%".',
  },
  INLAND_ORIGIN: {
    titulo: 'Terrestre en origen',
    ayuda: 'Del depósito del proveedor al puerto de embarque.',
  },
  INLAND_DESTINATION: {
    titulo: 'Terrestre en destino',
    ayuda: 'Del puerto de tránsito hasta la ciudad del cliente.',
  },
  HANDLING: {
    titulo: 'Manipuleo y servicios',
    ayuda: 'Gastos de liberación, almacenaje y servicios sueltos.',
  },
};

/** Un ejemplo con números reales del tarifario, para ver el efecto. */
const COSTO_EJEMPLO = 5945;

export function MarginSettings() {
  const { toast } = useToast();
  const [rows, setRows] = useState<MarginRow[] | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<LegType | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await axiosInstance.get<{ data: { margins: MarginRow[] } }>('/api/trade/margenes');
      setRows(r.data.data.margins);
    } catch {
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (legType: LegType, value: string) => {
    const marginPercent = Number(value.replace(',', '.'));
    if (!Number.isFinite(marginPercent) || marginPercent < 0 || marginPercent > 500) {
      toast({
        title: 'Margen inválido',
        description: 'Tiene que ser un número entre 0 y 500.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(legType);
    try {
      const r = await axiosInstance.put<{ data: { margins: MarginRow[] } }>('/api/trade/margenes', {
        legType,
        marginPercent,
      });
      setRows(r.data.data.margins);
      setDraft((d) => {
        const next = { ...d };
        delete next[legType];
        return next;
      });
      toast({
        title: 'Margen actualizado',
        description: 'Las cotizaciones nuevas ya lo usan. Las guardadas no cambian.',
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: 'No se pudo guardar',
        description:
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          'Reintentá en un momento.',
        variant: 'destructive',
      });
    } finally {
      setSaving(null);
    }
  };

  if (rows === null) {
    return (
      <div className="space-y-2">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/*
        No se repite lo que ya dice la descripción de la tarjeta. Lo único que
        agrega valor acá es la naturaleza del número: es una sugerencia, no un
        precio impuesto.
      */}
      <p className="text-xs leading-relaxed text-muted-foreground">
        En la cotización el precio queda editable:{' '}
        <strong>esto define la sugerencia, no la última palabra</strong>.
      </p>

      <div className="overflow-hidden rounded-lg border">
        {rows.map((row, i) => {
          const editing = draft[row.legType] !== undefined;
          const value = editing ? draft[row.legType] : String(row.marginPercent);
          const numeric = Number(String(value).replace(',', '.'));
          const preview = Number.isFinite(numeric)
            ? Math.round(COSTO_EJEMPLO * (1 + numeric / 100) * 100) / 100
            : null;

          return (
            <div
              key={row.legType}
              className={cn(
                'flex flex-wrap items-center justify-between gap-3 px-3 py-2.5',
                i > 0 && 'border-t'
              )}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">
                  {ETIQUETAS[row.legType].titulo}
                  {row.isDefault && (
                    <span className="ml-2 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-normal text-muted-foreground">
                      por defecto
                    </span>
                  )}
                </p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {ETIQUETAS[row.legType].ayuda}
                </p>
              </div>

              <div className="flex items-center gap-2">
                {/*
                  El efecto se ve mientras se escribe, con un costo real del
                  tarifario. Un porcentaje suelto no dice nada; "5.945 → 6.539,50"
                  sí, y es la misma cuenta que va a ver el cliente.
                */}
                {preview !== null && (
                  <span className="hidden font-mono text-[11px] tabular-nums text-muted-foreground sm:inline">
                    {COSTO_EJEMPLO.toLocaleString('es-BO')} →{' '}
                    <span className="font-semibold text-foreground">
                      {preview.toLocaleString('es-BO', { minimumFractionDigits: 2 })}
                    </span>
                  </span>
                )}

                <div className="relative">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={value}
                    onChange={(e) => setDraft((d) => ({ ...d, [row.legType]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void save(row.legType, value);
                      if (e.key === 'Escape')
                        setDraft((d) => {
                          const n = { ...d };
                          delete n[row.legType];
                          return n;
                        });
                    }}
                    className="h-8 w-20 pr-6 text-right font-mono text-sm tabular-nums"
                    aria-label={`Margen de ${ETIQUETAS[row.legType].titulo}`}
                  />
                  <Percent className="pointer-events-none absolute right-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
                </div>

                {editing ? (
                  <>
                    <Button
                      size="sm"
                      className="h-8"
                      disabled={saving === row.legType}
                      onClick={() => void save(row.legType, value)}
                    >
                      {saving === row.legType ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Check className="h-3.5 w-3.5" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8"
                      onClick={() =>
                        setDraft((d) => {
                          const n = { ...d };
                          delete n[row.legType];
                          return n;
                        })
                      }
                      title="Descartar el cambio"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                    </Button>
                  </>
                ) : (
                  <span className="w-[68px]" aria-hidden />
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[11px] leading-relaxed text-muted-foreground">
        Cambiar un margen afecta las cotizaciones <strong>nuevas</strong>. Las ya guardadas
        mantienen su precio: una cotización enviada al cliente es un compromiso, y recalcularla
        sería cambiarle el precio a alguien que ya lo recibió.
      </p>
    </div>
  );
}
